import { useCallback, useEffect, useState } from "react";
import { ChevronLeft, Plus } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { ActionButton, ListRow, SecondaryAction } from "../components/kit";
import LoadFailedCard from "../components/pickxi/LoadFailedCard";
import GamesEmpty, { SCHEDULE_PATH } from "../components/pickxi/GamesEmpty";
import PickXiChrome from "../components/pickxi/PickXiChrome";
import { listGames, gameStatusLabel, gameWhenLine } from "../lib/coachGames";

/**
 * Issue #1316 (Pick XI coach 1 of 3; D297, D309) — `/coach-portal/games`,
 * the board's `games-list`: Your games, soonest first, each marked Team
 * published, Draft or Team not picked. Under D309 Team selection opens on the
 * next game's team sheet, so this list sits one tap further (the team sheet's
 * "Your games" link). A row opens that game's team sheet,
 * `/coach-portal/team?game=<id>`. Today onwards only (the API's list).
 *
 * Loading, failed and empty are three separate states (D53). #1430 (D386):
 * the list is `GamesColumn`, exported so `CoachTeam` can draw the same column
 * beside a game's team sheet from the laptop tier (the open game marked,
 * `selectedId`); on its own, with no game open, it is the one 640px column.
 */
export function GamesColumn({ selectedId = null }) {
  const navigate = useNavigate();
  const [state, setState] = useState({ status: "loading", games: [] });

  const load = useCallback(() => {
    let live = true;
    setState({ status: "loading", games: [] });
    listGames()
      .then((games) => live && setState({ status: "ready", games }))
      .catch((e) => {
        console.error("CoachGames: failed to load the games", e);
        if (live) setState({ status: "failed", games: [] });
      });
    return () => {
      live = false;
    };
  }, []);

  useEffect(() => load(), [load]);

  const schedule = () => navigate(SCHEDULE_PATH);

  let body;
  if (state.status === "loading") {
    body = (
      <div data-testid="games-loading" aria-busy="true" className="flex flex-col" style={{ gap: 8 }}>
        {[0, 1, 2].map((i) => (
          <div key={i} style={{ height: 76, background: "var(--nw-surface)", border: "1px solid var(--nw-line)", borderRadius: "var(--nw-radius-card)" }} />
        ))}
      </div>
    );
  } else if (state.status === "failed") {
    body = <LoadFailedCard title="Couldn't load your games" testId="games-failed" retryTestId="games-failed-retry" onRetry={load} />;
  } else if (state.games.length === 0) {
    body = <GamesEmpty />;
  } else {
    body = (
      <>
        {selectedId ? (
          // Beside a team sheet the sheet owns the screen's one gold (R-003), so the list's action is the boxed one.
          <SecondaryAction variant="navy-outline" data-testid="games-new" onClick={schedule} style={{ gap: 8 }}>
            <Plus size={18} strokeWidth={2.4} aria-hidden="true" />
            Schedule a game
          </SecondaryAction>
        ) : (
          <ActionButton data-testid="games-new" icon={<Plus size={18} strokeWidth={2.4} />} onClick={schedule}>
            Schedule a game
          </ActionButton>
        )}
        <div className="flex flex-col" style={{ gap: 8 }} data-testid="games-list">
          {state.games.map((g) => (
            <ListRow
              key={g.game_id}
              data-testid={`games-row-${g.game_id}`}
              name={`vs ${g.opponent}`}
              figuresLine={gameWhenLine(g)}
              tag={gameStatusLabel(g.status)}
              chevron
              onClick={() => navigate(`/coach-portal/team?game=${encodeURIComponent(g.game_id)}`)}
              {...(selectedId === g.game_id ? { "aria-current": "true" } : {})}
              style={{ cursor: "pointer", ...(selectedId === g.game_id ? { borderColor: "var(--nw-navy)" } : {}) }}
            />
          ))}
        </div>
      </>
    );
  }

  return (
    <div className="flex flex-col" style={{ gap: 14, padding: "var(--nw-space-page)" }}>
      <SecondaryAction as={Link} to="/coach-portal" variant="link" data-testid="games-back" style={{ display: "flex", width: "fit-content", alignItems: "center", gap: 4 }}>
        <ChevronLeft size={16} aria-hidden="true" />
        Your players
      </SecondaryAction>
      <div className="flex flex-col" style={{ gap: 4 }}>
        <div className="nw-eyebrow">Team selection</div>
        <div className="nw-title-lg" data-testid="games-title">Your games</div>
      </div>
      {body}
    </div>
  );
}

export default function CoachGames() {
  return (
    <PickXiChrome title="Your games" testId="games-page">
      <GamesColumn />
    </PickXiChrome>
  );
}
